import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import KanbanBoard from '../kanban/KanbanBoard'
import GymList from '../gyms/GymList'
import GoLivePanel from '../../components/ceo/GoLivePanel'
import UserManagement from '../../components/ceo/UserManagement'
import ExpiredClients from '../expired-clients/ExpiredClients'
import PreRekiChecklist from '../checklists/PreRekiChecklist'
import MetricDetailModal from '../../components/modals/MetricDetailModal'
import GymTimelineModal from '../../components/modals/GymTimelineModal'
import axios from 'axios'
import { toast } from 'react-toastify'
import { format, isAfter, addDays } from 'date-fns'

const CEODashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [metricModal, setMetricModal] = useState({
    isOpen: false,
    metricType: null,
    stageName: null
  })
  const [timelineModal, setTimelineModal] = useState({
    isOpen: false,
    gymId: null,
    gymName: null
  })

  const openTimelineModal = (gymId, gymName) => {
    setTimelineModal({
      isOpen: true,
      gymId,
      gymName
    })
  }

  const closeTimelineModal = () => {
    setTimelineModal({
      isOpen: false,
      gymId: null,
      gymName: null
    })
  }

  const openMetricModal = (metricType, stageName = null) => {
    setMetricModal({
      isOpen: true,
      metricType,
      stageName
    })
  }

  const closeMetricModal = () => {
    setMetricModal({
      isOpen: false,
      metricType: null,
      stageName: null
    })
  }

  console.log('🎯 CEO Dashboard component starting...', { user })

  // Verify CEO access
  if (!user || user.role.level !== 1) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-700">This dashboard is restricted to CEO access only.</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get('/api/ceo/dashboard')
      setDashboardData(response.data)
    } catch (error) {
      console.error('Error fetching CEO dashboard data:', error)
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message
      setError(errorMessage)
      toast.error(`Failed to load dashboard data: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'kanban':
        return renderKanbanTab()
      case 'gyms':
        return renderAllGymsTab()
      case 'golive':
        return renderGoLiveTab()
      case 'users':
        return renderUserManagementTab()
      case 'expired':
        return renderExpiredClientsTab()
      case 'pre-reki':
        return renderPreRekiTab()
      case 'post-reki':
        return renderPostRekiTab()
      default:
        return renderDashboardTab()
    }
  }

  const renderDashboardTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Key Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Executive Metrics</h3>
        {dashboardData ? (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => openMetricModal('totalGyms')}
              className="text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-blue-300"
            >
              <div className="text-2xl font-bold text-blue-600">{dashboardData.metrics.totalGyms}</div>
              <div className="text-sm text-blue-700">Total Gyms</div>
              <div className="text-xs text-blue-500 mt-1">Click to view details</div>
            </button>
            <button
              onClick={() => openMetricModal('pendingApprovals')}
              className="text-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-orange-300"
            >
              <div className="text-2xl font-bold text-orange-600">{dashboardData.metrics.pendingApprovals}</div>
              <div className="text-sm text-orange-700">Pending CEO Approval</div>
              <div className="text-xs text-orange-500 mt-1">Click to view details</div>
            </button>
            <button
              onClick={() => openMetricModal('completionRate')}
              className="text-center p-4 bg-green-50 rounded-lg hover:bg-green-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-green-300"
            >
              <div className="text-2xl font-bold text-green-600">{dashboardData.performanceIndicators.completionRate}%</div>
              <div className="text-sm text-green-700">Completion Rate</div>
              <div className="text-xs text-green-500 mt-1">Click to view details</div>
            </button>
            <button
              onClick={() => openMetricModal('overdueGyms')}
              className="text-center p-4 bg-red-50 rounded-lg hover:bg-red-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-red-300"
            >
              <div className="text-2xl font-bold text-red-600">{dashboardData.metrics.overdueGyms}</div>
              <div className="text-sm text-red-700">Overdue Gyms</div>
              <div className="text-xs text-red-500 mt-1">Click to view details</div>
            </button>
          </div>
        ) : (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}
      </div>

      {/* Stage Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Overview</h3>
        {dashboardData ? (
          <div className="space-y-2">
            {Object.entries(dashboardData.stageBreakdown).map(([stage, count]) => (
              <button
                key={stage}
                onClick={() => openMetricModal('stage', stage)}
                className="w-full flex justify-between items-center p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <span className="text-sm text-gray-600">{stage}</span>
                <span className="font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded">{count}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}
      </div>

      {/* Recent Go-Lives */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Go-Lives (Last 7 Days)</h3>
        {dashboardData && dashboardData.recentGoLives.length > 0 ? (
          <div className="space-y-3">
            {dashboardData.recentGoLives.map((gym, index) => (
              <button
                key={index}
                onClick={() => openTimelineModal(gym.id, gym.name)}
                className="w-full flex justify-between items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-green-300"
              >
                <div className="text-left">
                  <div className="font-medium text-green-900">{gym.name}</div>
                  <div className="text-sm text-green-700">{gym.location}</div>
                  <div className="text-xs text-green-500 mt-1">Click to view timeline</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-green-600">
                    {format(new Date(gym.updated_at), 'MMM dd')}
                  </div>
                  <div className="text-xs text-green-500">
                    {gym.assignedUser ? gym.assignedUser.name : 'Unassigned'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>No recent Go-Lives</p>
          </div>
        )}
      </div>

      {/* Team Performance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Overview</h3>
        {dashboardData ? (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => openMetricModal('activeUsers')}
              className="text-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-purple-300"
            >
              <div className="text-2xl font-bold text-purple-600">{dashboardData.metrics.activeUsers}</div>
              <div className="text-sm text-purple-700">Active Users</div>
              <div className="text-xs text-purple-500 mt-1">Click to view details</div>
            </button>
            <button
              onClick={() => openMetricModal('onTimeDelivery')}
              className="text-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-indigo-300"
            >
              <div className="text-2xl font-bold text-indigo-600">{dashboardData.performanceIndicators.onTimeDelivery}%</div>
              <div className="text-sm text-indigo-700">On-Time Delivery</div>
              <div className="text-xs text-indigo-500 mt-1">Click to view details</div>
            </button>
          </div>
        ) : (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}
      </div>
    </div>
  )

  const renderKanbanTab = () => (
    <div>
      <KanbanBoard />
    </div>
  )

  const renderAllGymsTab = () => (
    <div>
      <GymList />
    </div>
  )

  const renderGoLiveTab = () => (
    <div>
      <GoLivePanel />
    </div>
  )

  const renderUserManagementTab = () => (
    <div>
      <UserManagement />
    </div>
  )

  const renderExpiredClientsTab = () => (
    <div>
      <ExpiredClients />
    </div>
  )

  const renderPreRekiTab = () => (
    <div>
      <PreRekiChecklist />
    </div>
  )

  const renderPostRekiTab = () => (
    <div>
      <PreRekiChecklist />
    </div>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Dashboard</h2>
          <p className="text-red-700">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-lg shadow-sm p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Good Evening, {user?.firstName}! 👑</h1>
            <p className="text-red-100 mt-1">
              CEO - Gym Acquisition Dashboard
            </p>
            <p className="text-red-200 text-sm mt-1">
              Complete system oversight and final Go-Live authority
            </p>
          </div>
          <div className="text-right">
            <div className="text-red-100 text-sm">Your Authority Scope</div>
            <div className="text-3xl font-bold">
              {dashboardData?.metrics.totalGyms || 0}
            </div>
            <div className="text-red-200 text-sm">Total gyms under management</div>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div
          onClick={() => openMetricModal('totalGyms')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h4M9 7h6m-6 4h6m-6 4h6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Gyms</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.metrics.totalGyms || 0}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('pendingApprovals')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-orange-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.metrics.pendingApprovals || 0}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('completionRate')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-green-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed This Week</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.metrics.recentGoLives || 0}</p>
            </div>
          </div>
        </div>

        <div
          onClick={() => openMetricModal('totalGyms')}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md hover:border-purple-300 transition-all"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">My Scope</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData?.metrics.totalGyms || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'dashboard' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'kanban' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('kanban')}
          >
            View Kanban
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'gyms' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('gyms')}
          >
            View All Gyms
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'golive' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('golive')}
          >
            Go-Live Center
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'users' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'expired' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('expired')}
          >
            Expired Clients
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'pre-reki' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('pre-reki')}
          >
            Pre-Reki
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'post-reki' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            onClick={() => setActiveTab('post-reki')}
          >
            Post-Reki
          </button>
        </div>
        <div className="mt-4">
          {renderTabContent()}
        </div>
      </div>

      {/* Metric Detail Modal */}
      <MetricDetailModal
        isOpen={metricModal.isOpen}
        onClose={closeMetricModal}
        metricType={metricModal.metricType}
        stageName={metricModal.stageName}
      />

      {/* Gym Timeline Modal */}
      <GymTimelineModal
        isOpen={timelineModal.isOpen}
        onClose={closeTimelineModal}
        gymId={timelineModal.gymId}
        gymName={timelineModal.gymName}
      />
    </div>
  )
}

export default CEODashboard 